import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Upload, CheckCircle2, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

// Time before showing force submit option (3 minutes)
const FORCE_SUBMIT_DELAY_MS = 3 * 60 * 1000;

interface TestSubmissionConfirmationDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  pendingUploadsCount?: number;
  failedUploadsCount?: number;
  hasVideoResponses?: boolean;
  onForceSubmit?: () => void;
}

export function TestSubmissionConfirmationDialog({
  open,
  onConfirm,
  onCancel,
  pendingUploadsCount = 0,
  failedUploadsCount = 0,
  hasVideoResponses = false,
  onForceSubmit,
}: TestSubmissionConfirmationDialogProps) {
  const [showForceSubmit, setShowForceSubmit] = useState(false);
  const [waitingTime, setWaitingTime] = useState(0);
  
  const hasPendingUploads = pendingUploadsCount > 0;
  const hasFailures = failedUploadsCount > 0;
  const canSubmit = !hasPendingUploads;

  // Track how long we've been waiting with pending uploads
  useEffect(() => {
    if (!open || !hasPendingUploads) {
      setShowForceSubmit(false);
      setWaitingTime(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setWaitingTime(elapsed);
      if (elapsed >= FORCE_SUBMIT_DELAY_MS) {
        setShowForceSubmit(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [open, hasPendingUploads]);

  const formatWaitTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
              hasPendingUploads 
                ? 'bg-blue-500/10' 
                : hasFailures
                ? 'bg-orange-500/10'
                : 'bg-green-500/10'
            }`}>
              {hasPendingUploads ? (
                <Upload className="h-6 w-6 text-blue-500 animate-pulse" />
              ) : hasFailures ? (
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              ) : (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              )}
            </div>
            <AlertDialogTitle className="text-xl">
              {hasPendingUploads 
                ? "Finalizing Responses..." 
                : "Submit Test?"}
            </AlertDialogTitle>
          </div>
          
          {hasPendingUploads ? (
            <div className="space-y-3 pt-2">
              <AlertDialogDescription className="text-base">
                Your video responses are being uploaded in the background. 
                Please wait for all uploads to complete before submitting your test.
              </AlertDialogDescription>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {pendingUploadsCount} {pendingUploadsCount === 1 ? 'upload' : 'uploads'} remaining
                  </span>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground font-mono text-xs">
                      {formatWaitTime(waitingTime)}
                    </span>
                    <Upload className="h-4 w-4 text-blue-500 animate-pulse" />
                  </div>
                </div>
                <Progress value={undefined} className="h-2" indicatorClassName="bg-blue-500" />
              </div>
              
              {showForceSubmit ? (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-md">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-amber-700 dark:text-amber-400">
                      Uploads are taking longer than expected. You can force submit now, but some video responses may not be saved.
                    </span>
                  </div>
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={onForceSubmit || onConfirm}
                  >
                    Force Submit (Some videos may be lost)
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  The "Submit Test" button will be enabled once all uploads are complete.
                  {waitingTime > 60000 && " If this takes too long, a force submit option will appear."}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <AlertDialogDescription className="text-base">
                Are you sure you want to submit your test? You won't be able to make
                any changes after submission. Please review your answers before
                proceeding.
              </AlertDialogDescription>
              
              {/* Show upload failures */}
              {hasFailures && (
                <div className="flex items-center gap-2 p-3 bg-orange-500/10 rounded-md">
                  <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span className="text-sm text-orange-700 dark:text-orange-400">
                    {failedUploadsCount} {failedUploadsCount === 1 ? 'upload' : 'uploads'} failed. 
                    Some video/audio responses may not be saved.
                  </span>
                </div>
              )}
              
              {/* Show success message */}
              {!hasFailures && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-md">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    All responses have been saved successfully
                  </span>
                </div>
              )}
            </div>
          )}
        </AlertDialogHeader>
        
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {hasPendingUploads ? "Keep Waiting" : "Go Back"}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            disabled={!canSubmit}
            className={hasFailures ? "bg-orange-500 hover:bg-orange-600" : ""}
          >
            {hasFailures ? "Submit Anyway" : "Submit Test"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
